import React from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useFieldArray, useFormState, useWatch } from 'react-hook-form'
import { ScrollRestore, FormSectionTitle, PairingButton } from 'src/Libs/flow/components'
import { getDeviceDefaultName } from '../../../parsers/device-parsers/get-device-default-name'
import { checkTrueRecursively } from '../../../utils/check-true-recursively'
import { TFunction } from 'i18next'
import useModals from 'src/Hooks/useModals'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { HideDirtyStateProvider } from '../../providers'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { useDevicePairing } from '../../hooks'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'
import { deleteIndividualDevice } from 'src/Libs/flow/installation/api/save-installation/get-installation-delete-requests'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { SettingsBox } from 'src/Elements/Data'
import { NumberField, Select, TextField } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

const POWER_METER_WRONGLY_WIRED_STATUS_CODE = 16

//#region Main Component

/**
 * Displays a list of meters with respective settings to edit.
 */
export const Meters: React.FC = () => {
  const { control } = useInstallationForm()
  const { fields } = useFieldArray({
    control,
    name: 'meters',
  })

  return (
    <>
      <BannerQueue location={BannerLocations.FlowSettingsAdvancedSettingsPowerMeter} sx={{ mt: [2, 0], mb: [0, 2] }} />
      <Box mb={[8, 0]}>
        {fields.map((item, index) => (
          <Meter key={item.id} index={index} />
        ))}
        <ScrollRestore />
      </Box>
    </>
  )
}

// #region Sub Components

const Meter: React.FC<{ index: number }> = ({ index }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const { openConfirmationModal, openInstallationDeleteDialog } = useModals()

  const theme = useTheme()
  const upSm = useMediaQuery(theme.breakpoints.up('sm'))
  const fullWidth = !upSm

  const { control, getValues, setValue, reset } = useInstallationForm()
  const { dirtyFields, errors } = useFormState({ control, name: `meters.${index}` })

  const isDirty = checkTrueRecursively(dirtyFields.meters?.[index])
  const isError = !!Object.keys(errors.meters?.[index] ?? {}).length

  const meter = useWatch({
    control,
    name: `meters.${index}`,
  })
  const { isNew, name, deviceType, pairingId, deviceId, navigationId, deviceStatusCodeBits } = meter

  const isPvExternal = deviceType === 'PVExtern'

  const { canPair, handlePairing } = useDevicePairing(meter)

  const displayName = getDeviceDefaultName(name, 'Meter', deviceId, t)

  const handleDelete = (): void => {
    openConfirmationModal({
      title: t('installation.deleteConfirmation.title'),
      text: `${t('installation.deleteConfirmation.selectedDevices')} ${displayName}`,
      buttonText: [t('common.cancel'), t('common.confirm')],
      onConfirm: async (onClose): Promise<void> => {
        const device = getValues(`meters.${index}`)
        Promise.resolve().then(() => {
          deleteIndividualDevice(device, getValues, setValue, openInstallationDeleteDialog, reset, t, authFetch)
        })
        onClose()
      },
    })
  }

  return (
    <HideDirtyStateProvider hideDirtyState={isNew}>
      <AccordionItem title={<FormSectionTitle header="h2" name={displayName} onClick={handleDelete} isDirty={isDirty} isError={isError} />} expanded>
        <Box sx={{ p: 2 }} id={navigationId}>
          <SettingsBox
            title={t('installation.submeterEditor.deviceName')}
            suffix={
              <TextField
                label={t('installation.submeterEditor.deviceName')}
                name={`meters.${index}.name`}
                maxLength={100}
                fullWidth={fullWidth}
                disabled={!canEdit}
              />
            }
          />
          <SettingsBox
            title={t('productEditor.pairingEditor.pairingID.submeter')}
            suffix={
              <SubmeterSuffix
                index={index}
                pairingId={pairingId}
                deviceId={deviceId}
                handlePairing={handlePairing}
                canPair={canPair && canEdit}
                upSm={upSm}
              />
            }
          />
          <SettingsBox
            title={t('installation.submeterEditor.deviceType')}
            suffix={
              <TextField
                label={t('installation.submeterEditor.deviceType')}
                name={`meters.${index}.deviceType`}
                value={meterDeviceTypeMessage(t)[deviceType]}
                disabled
                fullWidth={fullWidth}
              />
            }
          />
          {isPvExternal && (
            <SettingsBox
              title={t('installation.submeterEditor.peakPower')}
              suffix={
                <NumberField
                  name={`meters.${index}.peakPower`}
                  label={t('installation.submeterEditor.peakPower')}
                  inputProps={{ min: 0, max: 2_000_000 }}
                  fullWidth={fullWidth}
                  suffix={UnitTypes.wattPeak}
                  disabled={!canEdit}
                />
              }
            />
          )}
          <SettingsBox
            title={t('installation.submeterEditor.wiring-mode')}
            suffix={
              <Select
                name={`meters.${index}.wiringMode`}
                label={t('installation.submeterEditor.wiring-mode')}
                options={meterWiringModeOptions(t)}
                fullWidth={fullWidth}
                disabled={!canEdit}
              />
            }
          />
          {deviceStatusCodeBits === POWER_METER_WRONGLY_WIRED_STATUS_CODE && (
            <Typography variant="subtitle1" color={'error'}>
              {t('installation.submeterEditor.warning.inverted-wiring-info')}
            </Typography>
          )}
        </Box>
      </AccordionItem>
    </HideDirtyStateProvider>
  )
}

interface SubmeterSuffixProps {
  index: number
  pairingId: string | null
  deviceId: string | null
  handlePairing: () => void
  canPair: boolean
  upSm: boolean
}

const SubmeterSuffix: React.FC<SubmeterSuffixProps> = ({ index, pairingId, deviceId, handlePairing, canPair, upSm }) => {
  const { t } = useCustomTranslation()

  const getButtonText = (): string => {
    if (!deviceId) return t('common.edit')
    if (pairingId) return t('common.replace')
    return t('common.pair')
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: upSm ? 'row' : 'column', width: '100%', justifyContent: 'flex-end' }}>
      <TextField label={t('productEditor.pairingEditor.pairingID.submeter')} name={`meters.${index}.pairingId`} disabled fullWidth={!upSm} />
      <PairingButton size="large" sx={{ height: '100%' }} onClick={handlePairing} disabled={!canPair} icon={pairingId ? 'transfer' : 'link'}>
        {getButtonText()}
      </PairingButton>
    </Box>
  )
}

// #region Helpers

const meterDeviceTypeMessage = (t: TFunction): Record<InstallationFormModel['meters'][number]['deviceType'], string> => ({
  HVAC: t('installation.submeterEditor.deviceTypeOptions.hvac'),
  PVExtern: t('installation.submeterEditor.deviceTypeOptions.pv'),
  Evse: t('installation.submeterEditor.deviceTypeOptions.evse'),
  House: t('installation.submeterEditor.deviceTypeOptions.house'),
})

const meterWiringModeOptions = (t: TFunction): Options<InstallationFormModel['meters'][number]['wiringMode']> => [
  { value: 'Normal', label: t('installation.submeterEditor.wiring-mode-label.normal') },
  { value: 'Inverted', label: t('installation.submeterEditor.wiring-mode-label.inverted') },
]
